/**
 * Discovery, the two filter sliders (H56). Pure: no I/O.
 *
 * **The slider is a picture of a value, never a new rule.** The price slider
 * runs in whole-dollar steps because H12 compares the cheapest service against
 * any ceiling, so every step is a value the server already accepts. The rating
 * slider snaps to H19's buckets and nothing between them, because those
 * buckets are the rule. Its **last** stop is "Any price", so dragging all the
 * way up switches the filter off; rating's **first** stop is "Any rating".
 *
 * The scale is ported number for number to both apps (`FilterScales` on iOS
 * and Android). Change it here and there in the same change.
 */
import { RATING_BUCKETS } from "./searchParams";

/** The price slider, in dollars: from $10 to $200 in $5 steps, then "Any". */
export const PRICE_SLIDER_MIN_DOLLARS = 10;
export const PRICE_SLIDER_MAX_DOLLARS = 200;
export const PRICE_SLIDER_STEP_DOLLARS = 5;

/** The number of stops, "Any price" included. */
export const PRICE_SLIDER_STOPS =
  (PRICE_SLIDER_MAX_DOLLARS - PRICE_SLIDER_MIN_DOLLARS) / PRICE_SLIDER_STEP_DOLLARS + 2;

/** The slider's last stop, which means no price filter. */
export const PRICE_SLIDER_ANY = PRICE_SLIDER_STOPS - 1;

/** A slider stop to the ceiling it stands for, or null at "Any price". */
export function priceFromStop(stop: number): number | null {
  const s = Math.round(stop);
  if (!Number.isFinite(s) || s >= PRICE_SLIDER_ANY) return null;
  const clamped = Math.max(0, s);
  return (PRICE_SLIDER_MIN_DOLLARS + clamped * PRICE_SLIDER_STEP_DOLLARS) * 100;
}

/**
 * Where a ceiling sits on the slider. A ceiling between stops (a shared link
 * with `maxPrice=2399`) sits at the stop at or above it, so the handle never
 * claims a stricter filter than the one applied; the label still states the
 * real value. Above the top of the scale it sits at the top priced stop.
 */
export function stopForPrice(maxPriceCents: number | null): number {
  if (maxPriceCents === null) return PRICE_SLIDER_ANY;
  const dollars = maxPriceCents / 100;
  const stop = Math.ceil((dollars - PRICE_SLIDER_MIN_DOLLARS) / PRICE_SLIDER_STEP_DOLLARS);
  return Math.min(PRICE_SLIDER_ANY - 1, Math.max(0, stop));
}

/** "Up to $45", or "Any price". */
export function priceSliderLabel(maxPriceCents: number | null): string {
  if (maxPriceCents === null) return "Any price";
  const dollars = maxPriceCents / 100;
  const text = Number.isInteger(dollars) ? String(dollars) : dollars.toFixed(2);
  return `Up to $${text}`;
}

/** The rating slider's stops: "Any rating", then H19's buckets, low to high. */
export const RATING_SLIDER_VALUES: readonly (number | null)[] = [
  null,
  ...[...RATING_BUCKETS].sort((a, b) => a - b),
];

export function ratingFromStop(stop: number): number | null {
  const s = Math.min(RATING_SLIDER_VALUES.length - 1, Math.max(0, Math.round(stop)));
  return RATING_SLIDER_VALUES[s] ?? null;
}

/** Where a minimum sits; anything off the bucket list is "Any rating". */
export function stopForRating(minRating: number | null): number {
  const index = RATING_SLIDER_VALUES.indexOf(minRating);
  return index < 0 ? 0 : index;
}

/** "4★ & up", or "Any rating". */
export function ratingSliderLabel(minRating: number | null): string {
  return minRating === null ? "Any rating" : `${minRating}★ & up`;
}
